// ==============================================
// Club Details & Booking Page
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { clubsAPI } from '../services/api';
import { Club, ClubTable } from '../types';
import { idFromSlug } from '../utils/slug';
import BookingCalendar from '../components/BookingCalendar';

const InfoChip: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '.5rem',
    background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
    borderRadius: 10, padding: '.6rem 1rem', fontSize: '.85rem', color: 'var(--tt-text-2)',
    whiteSpace: 'nowrap',
  }}>
    <span>{icon}</span> {label}
  </div>
);

const ClubDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const id = idFromSlug(slug || '0');

  const [club, setClub] = useState<Club | null>(null);
  const [tables, setTables] = useState<ClubTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [clubRes, tablesRes] = await Promise.all([
        clubsAPI.getById(id),
        clubsAPI.getTables(id, 'available'),
      ]);
      if (clubRes.success && clubRes.data) setClub(clubRes.data);
      else { setError('Club negăsit'); return; }
      if (tablesRes.success && tablesRes.data) setTables(tablesRes.data.tables || []);
    } catch {
      setError('Nu s-au putut încărca detaliile clubului.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleBookingSuccess = () => {
    setBookingSuccess(true);
    setTimeout(() => navigate('/bookings'), 2500);
  };

  // ── Loading ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="page">
        <div className="container page-inner text-center py-5">
          <span className="spinner-border" style={{ color: 'var(--tt-primary)' }} />
          <p className="mt-3 text-muted">Se încarcă detaliile clubului…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────
  if (error || !club) {
    return (
      <div className="page">
        <div className="container page-inner">
          <div className="alert alert-danger">{error || 'Club negăsit'}</div>
          <Link to="/clubs" className="btn btn-outline-primary">← Înapoi la cluburi</Link>
        </div>
      </div>
    );
  }

  const available = club.number_of_tables - (club.booked_tables_today ?? 0);

  // ── Main render ──────────────────────────────────────────
  return (
    <div className="page">
      <div className="container page-inner" style={{ maxWidth: 860 }}>

        {/* Back */}
        <Link to="/clubs" style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem', color: 'var(--tt-text-muted)', fontSize: '.875rem', textDecoration: 'none', marginBottom: '1rem' }}>
          ← Înapoi la cluburi
        </Link>

        {/* Booking success */}
        {bookingSuccess && (
          <div className="alert alert-success mb-4">
            ✓ Rezervare efectuată cu succes! Ești redirecționat la rezervările tale…
          </div>
        )}

        {/* ── Hero ── */}
        <div style={{
          position: 'relative', height: 280, borderRadius: 20, overflow: 'hidden',
          background: 'var(--tt-surface-2)', marginBottom: '1.5rem',
          boxShadow: '0 8px 32px rgba(0,0,0,.12)',
        }}>
          {club.image_url ? (
            <img src={club.image_url} alt={club.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5rem' }}>🏓</div>
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,.75) 0%, rgba(0,0,0,.1) 60%)' }} />

          {/* Overlay info */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1.5rem' }}>
            <div className="d-flex justify-content-between align-items-end">
              <div>
                <h1 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: 800, margin: 0, textShadow: '0 2px 8px rgba(0,0,0,.4)' }}>
                  {club.name}
                </h1>
                <div style={{ color: 'rgba(255,255,255,.8)', fontSize: '.9rem', marginTop: '.3rem' }}>
                  📍 {club.city_name}{club.address ? ` · ${club.address}` : ''}
                </div>
              </div>
              {club.rating != null && (
                <div style={{
                  background: 'rgba(255,255,255,.15)', backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,.25)', borderRadius: 12,
                  padding: '.5rem .9rem', textAlign: 'center',
                }}>
                  <div style={{ color: '#EF8628', fontSize: '1.1rem', fontWeight: 800 }}>★ {club.rating.toFixed(1)}</div>
                  <div style={{ color: 'rgba(255,255,255,.7)', fontSize: '.7rem' }}>{club.reviews_count ?? 0} recenzii</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Info chips ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.625rem', marginBottom: '1.5rem' }}>
          <InfoChip icon="🏓" label={`${club.number_of_tables} mese`} />
          {club.open_hours && <InfoChip icon="🕐" label={club.open_hours} />}
          {club.phone && <InfoChip icon="📞" label={club.phone} />}
          {club.email && <InfoChip icon="✉️" label={club.email} />}
          {club.website && (
            <a href={club.website} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <InfoChip icon="🌐" label="Website" />
            </a>
          )}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '.5rem',
            background: available > 0 ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${available > 0 ? '#bbf7d0' : '#fecaca'}`,
            borderRadius: 10, padding: '.6rem 1rem', fontSize: '.85rem',
            color: available > 0 ? '#15803d' : '#dc2626', fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            {available > 0 ? `✓ ${available} mese libere azi` : '✕ Complet azi'}
          </div>
        </div>

        {/* ── Amenities ── */}
        {club.amenities && club.amenities.length > 0 && (
          <div className="entity-card mb-4">
            <div className="entity-card-body">
              <h6 style={{ fontWeight: 700, marginBottom: '.75rem', color: 'var(--tt-text-muted)', fontSize: '.8rem', textTransform: 'uppercase', letterSpacing: '.06em' }}>Facilități</h6>
              <div className="d-flex flex-wrap gap-2">
                {club.amenities.map(a => (
                  <span key={a} style={{
                    background: 'var(--tt-primary)', color: '#fff',
                    borderRadius: 8, padding: '.3rem .8rem',
                    fontSize: '.82rem', fontWeight: 600,
                  }}>{a}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Description ── */}
        {club.description && (
          <div className="entity-card mb-4">
            <div className="entity-card-body">
              <h6 style={{ fontWeight: 700, marginBottom: '.75rem', color: 'var(--tt-text-muted)', fontSize: '.8rem', textTransform: 'uppercase', letterSpacing: '.06em' }}>Despre club</h6>
              <p style={{ color: 'var(--tt-text-2)', lineHeight: 1.7, margin: 0 }}>{club.description}</p>
            </div>
          </div>
        )}

        {/* ── Booking calendar ── */}
        <div className="entity-card">
          <div className="entity-card-body">
            <div className="mb-4">
              <h5 style={{ fontWeight: 800, margin: 0 }}>📅 Rezervă o masă</h5>
              <p style={{ color: 'var(--tt-text-muted)', fontSize: '.875rem', margin: '4px 0 0' }}>
                {tables.length > 0
                  ? `Selectează masa și intervalul orar dorit`
                  : 'Nicio masă disponibilă momentan'}
              </p>
            </div>

            {tables.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '2.5rem', background: 'var(--tt-surface-2)',
                borderRadius: 12, border: '1px solid var(--tt-border)',
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '.75rem' }}>😔</div>
                <p style={{ color: 'var(--tt-text-muted)', margin: 0 }}>
                  Nicio masă disponibilă momentan.
                </p>
              </div>
            ) : (
              <BookingCalendar
                club={club}
                tables={tables}
                onSuccess={handleBookingSuccess}
              />
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ClubDetails;
